import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { fmtRON } from "../components/ProductCard";
import { toast } from "sonner";
import { CreditCard, Banknote, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const Field = ({ label, testid, value, onChange, required, type = "text", placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1.5">{label}{required && " *"}</label>
    <input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full bg-[#0B0B0B] border border-[#00E5FF]/25 focus:border-[#00E5FF] px-4 py-3 text-sm" />
  </div>
);

export default function CheckoutPage() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", address: "", city: "", county: "", note: "" });
  const [method, setMethod] = useState("ramburs");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const r = await api.post("/shop/promo/validate", { code: promoInput.trim(), subtotal: total });
      if (r.data.valid) {
        setPromo(r.data);
        toast.success(`Cod ${r.data.code} aplicat: -${new Intl.NumberFormat("ro-RO").format(r.data.discount)} RON`);
      } else {
        setPromo(null);
        toast.error(r.data.message || "Cod invalid");
      }
    } catch { toast.error("Eroare la verificarea codului"); }
    finally { setPromoLoading(false); }
  };

  const discount = promo ? Math.min(promo.discount, total) : 0;
  const finalTotal = total - discount;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address || !form.city) {
      toast.error("Completeaza nume, telefon, adresa si oras"); return;
    }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Adresa de email este obligatorie (primesti confirmarea si factura pe email)"); return;
    }
    if (items.length === 0) { toast.error("Cosul e gol"); return; }
    setLoading(true);
    try {
      const r = await api.post("/shop/orders", {
        ...form, payment_method: method,
        promo_code: promo ? promo.code : "",
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        origin_url: window.location.origin,
      });
      if (method === "card" && r.data.checkout_url) {
        clear();
        window.location.href = r.data.checkout_url;
        return;
      }
      clear();
      setDone(r.data.order_number);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la plasarea comenzii");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="order-success" className="max-w-xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-6" />
        <h1 className="font-display text-3xl uppercase">Comanda <span className="text-[#00E5FF]">plasata!</span></h1>
        <div className="text-neutral-400 mt-4">Numarul comenzii tale:</div>
        <div data-testid="order-number" className="font-mono font-bold text-2xl text-[#00E5FF] mt-1">{done}</div>
        <p className="text-sm text-neutral-500 mt-4">Te contactam telefonic pentru confirmare, apoi expediem cu plata ramburs la curier.</p>
        <Link to="/magazin" className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-bold px-6 py-3 uppercase tracking-wider text-sm mt-8 hover:bg-[#5CEEFF] transition-colors">
          Inapoi la magazin <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-5xl mx-auto px-6 py-14">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-2">// Checkout</div>
      <h1 className="font-display text-4xl uppercase">Finalizeaza <span className="text-[#00E5FF]">comanda</span></h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-10 mt-10">
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nume complet" testid="checkout-name" value={form.customer_name} onChange={set("customer_name")} required />
            <Field label="Telefon" testid="checkout-phone" value={form.phone} onChange={set("phone")} required />
          </div>
          <Field label="Email" testid="checkout-email" type="email" value={form.email} onChange={set("email")} required />
          <Field label="Adresa (strada, numar, bloc...)" testid="checkout-address" value={form.address} onChange={set("address")} required />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Oras" testid="checkout-city" value={form.city} onChange={set("city")} required />
            <Field label="Judet" testid="checkout-county" value={form.county} onChange={set("county")} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 block mb-1.5">Observatii</label>
            <textarea data-testid="checkout-note" value={form.note} onChange={set("note")} rows={2}
              className="w-full bg-[#0B0B0B] border border-[#00E5FF]/25 focus:border-[#00E5FF] px-4 py-3 text-sm" />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-3 mt-4">// Metoda de plata</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <button type="button" data-testid="pay-ramburs-btn" onClick={() => setMethod("ramburs")}
                className={`border p-5 text-left transition-colors ${method === "ramburs" ? "border-[#00E5FF] bg-[#00E5FF]/5" : "border-neutral-800 hover:border-neutral-600"}`}>
                <Banknote className={`w-6 h-6 mb-2 ${method === "ramburs" ? "text-[#00E5FF]" : "text-neutral-500"}`} />
                <div className="font-bold text-sm uppercase">Ramburs la curier</div>
                <div className="text-xs text-neutral-500 mt-1">Platesti cash sau cu cardul cand primesti coletul</div>
              </button>
              <button type="button" data-testid="pay-card-btn" onClick={() => setMethod("card")}
                className={`border p-5 text-left transition-colors ${method === "card" ? "border-[#00E5FF] bg-[#00E5FF]/5" : "border-neutral-800 hover:border-neutral-600"}`}>
                <CreditCard className={`w-6 h-6 mb-2 ${method === "card" ? "text-[#00E5FF]" : "text-neutral-500"}`} />
                <div className="font-bold text-sm uppercase">Card online</div>
                <div className="text-xs text-neutral-500 mt-1">Plata securizata prin Stripe</div>
              </button>
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit border border-[#00E5FF]/20 bg-[#0B0B0B] p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-bold mb-4">// Sumar comanda</div>
          <div className="space-y-3">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm gap-3">
                <span className="text-neutral-400">{i.qty} × {i.name}</span>
                <span className="font-medium whitespace-nowrap">{fmtRON(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-neutral-800 mt-4 pt-4">
            <div className="flex gap-0">
              <input data-testid="promo-code-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                placeholder="Cod promo"
                className="flex-1 min-w-0 bg-black border border-[#00E5FF]/25 focus:border-[#00E5FF] px-3 py-2.5 text-sm font-mono uppercase" />
              <button data-testid="apply-promo-btn" type="button" onClick={applyPromo} disabled={promoLoading}
                className="bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] px-4 text-xs uppercase tracking-wider font-bold hover:bg-[#00E5FF]/25 transition-colors">
                {promoLoading ? "..." : "Aplica"}
              </button>
            </div>
            {promo && (
              <div data-testid="promo-applied-row" className="flex justify-between items-center text-sm mt-3 text-green-400">
                <span className="flex items-center gap-2">Reducere ({promo.code})
                  <button type="button" data-testid="remove-promo-btn" onClick={() => { setPromo(null); setPromoInput(""); }} className="text-neutral-500 hover:text-red-400 text-xs underline">sterge</button>
                </span>
                <span className="font-bold">-{fmtRON(discount)}</span>
              </div>
            )}
          </div>
          <div className="border-t border-neutral-800 mt-4 pt-4 flex justify-between items-center text-sm">
            <span className="text-neutral-400">Livrare</span>
            <span className="text-green-400 font-bold uppercase text-xs tracking-wider">Gratuită</span>
          </div>
          <div className="border-t border-neutral-800 mt-4 pt-4 flex justify-between items-center">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Total</span>
            <span data-testid="checkout-total" className="text-[#00E5FF] font-bold text-2xl">{fmtRON(finalTotal)}</span>
          </div>
          <button data-testid="place-order-btn" type="submit" disabled={loading || items.length === 0}
            className="w-full mt-6 inline-flex items-center justify-center gap-3 bg-[#00E5FF] hover:bg-[#5CEEFF] disabled:opacity-50 text-black font-bold px-6 py-4 uppercase tracking-wider text-sm transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {method === "card" ? "Plateste cu cardul" : "Trimite comanda"}
          </button>
          <div className="text-[10px] text-neutral-600 text-center mt-3">Trimitand comanda esti de acord sa te contactam pentru confirmare.</div>
        </div>
      </form>
    </div>
  );
}
